<!-- App.svelte -->
<script>
    import "./app.css";

    import { onMount, afterUpdate, onDestroy } from 'svelte';
    import toast, { Toaster } from 'svelte-french-toast';

    import Transition from "./lib/components/Transition.svelte";

    import { Route, router } from 'tinro';
    import { navigation } from "./lib/data/navigation.js";
    import {
        dialogTitle,
        dialogBody,
        linkConverter,
        clipboardStore
    } from "./lib/data/stores.js";

    import { linkConverterContent } from "./lib/data/content.js";

    import Navbar from "./lib/components/Navbar.svelte";
    import Home from "./routes/Home.svelte";
    import AllDatabases from "./routes/AllDatabases.svelte";
    import ByFormat from "./routes/ByFormat.svelte";
    import ByClass from "./routes/ByClass.svelte";
    import BootstrapColumn from "./lib/components/BootstrapColumn.svelte";
    import Announcer from "./lib/components/Announcer.svelte";
    import BackToTop from "./lib/components/BackToTop.svelte";
    import FAQ from "./lib/components/sidebar/FAQ.svelte";
    import ModalWindow from "./lib/components/ModalWindow.svelte";
    import YoutubeThumbnail from './lib/components/YoutubeThumbnail.svelte';
    import BootstrapCard from "./lib/components/BootstrapCard.svelte";
    import TextBlock from "./lib/components/TextBlock.svelte";
    import { sendGAEvent } from './lib/utilities/ga.js';

    // Set the router mode to use hash-based navigation
    router.mode.hash();

    let menuItems;
    let inputElement;
    let formSelect;
    let searchResults;
    let firstCardLink;

    /**
     * Handle the linking information dialog box
     * @function
     */
    let isOpen;
    const currentUrl = window.location.host;

    /**
     * Opens the link converter modal by setting relevant dialog content and updating the URL.
     * @function
     */
    const openLinkConverter = () => {
        const database = {
            title: linkConverterContent.title,
            link_instructions: linkConverterContent.link_instructions,
        };

        console.log('database: ', database);
        dialogTitle.set(database.title);
        dialogBody.set(database.link_instructions);
        linkConverter.set(database.link_converter);

        isOpen = true;
    };

    /**
     * Prevents form submission when the Enter key is pressed in specific input fields.
     * @param {KeyboardEvent} event - The keyboard event triggered.
     */
    function preventFormSubmit(event) {
        console.log("preventFormSubmit: ", event);

        if (
            (event.key === 'Enter' || event.keyCode === 13) &&
            event.target.nodeName === 'INPUT' && event.target.type === 'text'
        ) {
            event.preventDefault();
            // Focus the first card link after preventing form submission
            firstCardLink = document.querySelector('#resultsList .card:first-of-type h3 a');
            console.log("link: ", firstCardLink);
            if(firstCardLink) {
                firstCardLink.focus();
            }
        }
    }

    /**
     * Attaches the preventFormSubmit function as an event listener to the input element.
     * @function
     */
    function selectElement() {
        inputElement = document.getElementById('filterControl');
        if (inputElement) {
            inputElement.addEventListener('keydown', preventFormSubmit);
        }
    }

    /**
     * onMount lifecycle hook to handle initial setup, including opening the link converter
     * if specified in the query parameters and setting up keyboard navigation.
     */
    onMount(() => {
        const queryParams = router.location.query.get(); // Get query parameters as an object

        if (queryParams['link-converter']) {
            openLinkConverter();
        }

        menuItems = document.getElementsByClassName("keynav");
        selectElement();

        /**
         * Handles left and right arrow key navigation between menu items.
         * @param {KeyboardEvent} event - The keyboard event triggered.
         */
        const handleKeydown = (event) => {
            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                const focusedElement = document.activeElement;
                const focusedIndex = Array.from(menuItems).indexOf(focusedElement);

                if (event.key === 'ArrowRight') {
                    const nextIndex = (focusedIndex + 1) % menuItems.length;
                    menuItems[nextIndex].focus();
                } else {
                    const prevIndex = (focusedIndex - 1 + menuItems.length) % menuItems.length;
                    menuItems[prevIndex].focus();
                }
            }
        };

        // Attach keydown event listeners to all menu items
        Array.from(menuItems).forEach((menuItem) => {
            menuItem.addEventListener('keydown', handleKeydown);
        });

        // Cleanup event listeners on component destroy
        return () => {
            Array.from(menuItems).forEach((menuItem) => {
                menuItem.removeEventListener('keydown', handleKeydown);
            });
        };
    });

    /**
     * afterUpdate lifecycle hook to reattach event listeners after component updates.
     */
    afterUpdate(() => {
        selectElement();
    });

    /**
     * Copies the provided text to the clipboard and shows a toast notification.
     * Defaults to copying the current window's URL if no text is provided.
     * @param {string} [textToCopy=window.location.href] - The text to copy.
     */
    function copyToClipboard(textToCopy = window.location.href) {
        console.log('[App] copyToClipboard called with:', textToCopy);
        navigator.clipboard.writeText(textToCopy).then(() => {
            console.log(`[App] Clipboard write SUCCESS: ${textToCopy}`);
            toast.success(`Copied:\n${textToCopy}`);
        }).catch((error) => {
            console.error(`[App] Clipboard write FAILED:`, error);
            toast.error(`Could not copy`);
        });
    }

    /**
     * Handles the openDialog event from child components by sending a GA event and opening the modal.
     * @param {CustomEvent} event - The custom event containing event details and database information.
     */
    const open = (event) => {
        const { event: originalEvent, database } = event.detail;
        console.log('Original Event: ', originalEvent);
        console.log('Database: ', database);

        /**
         * @typedef {Object} GAEventParams
         * @property {string} category - The GA event category.
         * @property {string} action - The GA event action/name.
         * @property {string} label - The GA event label.
         * @property {string} currentTab - The current tab or route.
         */

        /**
         * Sends a Google Analytics event for opening link instructions.
         *
         * @type {GAEventParams}
         */
        const gaEventParams = {
            category: 'AllDatabases',
            action: 'open_link_instructions',
            label: database.title,
            currentTab: $router.path // Accessing the current route path
        };

        // 1. Send GA Event
        sendGAEvent(
            gaEventParams.category,
            gaEventParams.action,
            gaEventParams.label,
            gaEventParams.currentTab,
            () => {
                console.log('GA event for linking instructions fired.');
            }
        );

        // 2. Update the dialog content
        dialogTitle.set(database.title);
        dialogBody.set(database.link_instructions || database.vendor[0].link_instructions || '');
        linkConverter.set(database.link_converter);

        // 3. Modify the URL with query parameters to include 'link-converter'
        const url = new URL(window.location.href);
        const basePath = url.pathname + '#/';
        const hashParts = url.hash.split('?');
        const searchParams = new URLSearchParams(hashParts[1] || '');
        searchParams.set('link-converter', '1');
        window.history.pushState({}, '', `${basePath}?${searchParams.toString()}`);

        // 4. Open the modal
        isOpen = true;
    };

    /**
     * Closes the modal and cleans up dialog content and URL query parameters.
     * @function
     */
    const close = () => {
        // Clear the dialog content
        dialogTitle.set('');
        dialogBody.set('');
        linkConverter.set('');

        // Remove "?link-converter" from the hash-based URL
        const url = new URL(window.location.href);
        const basePath = url.pathname + '#/';
        const hashParts = url.hash.split('?');
        const searchParams = new URLSearchParams(hashParts[1] || '');
        searchParams.delete('link-converter');
        const newHash = searchParams.toString() ? `?${searchParams.toString()}` : '';
        window.history.pushState({}, '', `${basePath}${newHash}`);

        // Close the modal
        isOpen = false;
    };

    /**
     * Watches the clipboardStore and triggers a copy to clipboard when it changes.
     */
    $: if ($clipboardStore) {
        console.log('[App] clipboardStore changed, value:', $clipboardStore);
        copyToClipboard($clipboardStore);
        console.log('[App] Resetting clipboardStore to empty');
        clipboardStore.set('');
    }

    /**
     * Generic GA event handler (optional, based on your usage)
     * @param {string} eventCategory - The GA event category.
     * @param {string} eventAction - The GA event action/name.
     * @param {string} eventLabel - The GA event label.
     */
    function handleGAEvent(eventCategory, eventAction, eventLabel) {
        const currentTab = $router.path;
        sendGAEvent(eventCategory, eventAction, eventLabel, currentTab, () => {
            console.log('GA event sent successfully');
        });
    }
</script>

<Announcer />
<Toaster />
<div class="row flex-lg-row-reverse">
    <BootstrapColumn classes="col-lg-4 order-2 order-lg-1 mb-4 mb-lg-0">

        <BootstrapCard classes="alert alert-info mb-4" >
            <div slot="content">
                <h3 class="m-0 p-0 mb-4">What is a database?</h3>
                <p>A library database is a searchable collection of content you can use for research in your classes and beyond. Most library databases are collections of articles, but we also have collections of eBooks, audiobooks, encyclopedias, videos, and more.</p>
            </div>
        </BootstrapCard>
        <FAQ
                on:openDialog={open}
                on:dismiss={close}
        />
        <BootstrapCard classes="alert alert-light mb-4" >
            <div slot="content">
                <h3 class="m-0 p-0 mb-4">Accessibility Help</h3>
                <p>These resources use digital content, images, or text that may cause barriers for those using assistive, or accessibility-related technologies.</p>
                <p>If you encounter barriers, please contact the PCC Access Tech Team at <a href="mailto:access-tech-group@pcc.edu?subject=databases%20page">access-tech-group@pcc.edu</a>
                    or by calling <a href="tel:9717228324">971-722-TECH</a>
                    (971-722-8324).</p>
            </div>
        </BootstrapCard>

        <YoutubeThumbnail
                on:dismiss={close}
        />

    </BootstrapColumn>

    <BootstrapColumn classes="col-lg-8 order-1 order-lg-2">
        <Navbar {navigation} />

        <Transition>

            <Route path="/#main" let:meta>
                <Home
                        content={navigation[0]}
                        on:openDialog={open}
                        on:copyToClipboard={(event) => copyToClipboard(event.detail || undefined)}
                />
            </Route>

            <Route path="/" let:meta>
                <Home
                        content={navigation[0]}
                        on:openDialog={open}
                        on:copyToClipboard={(event) => copyToClipboard(event.detail || undefined)}
                />
            </Route>

            <Route path="/by-class" let:meta>
                <ByClass
                        content={navigation[1]}
                        on:openDialog={open}
                        on:copyToClipboard={(event) => copyToClipboard(event.detail || undefined)}
                />
                <BackToTop />
            </Route>

            <Route path="/by-format" let:meta>
                <ByFormat
                        content={navigation[2]}
                        on:openDialog={open}
                        on:copyToClipboard={(event) => copyToClipboard(event.detail || undefined)}
                />
            </Route>

            <Route path="/all-databases" let:meta>
                <AllDatabases
                        content={navigation[3]}
                        on:openDialog={open}
                        on:copyToClipboard={(event) => copyToClipboard(event.detail || undefined)}
                />
                <BackToTop />
            </Route>
        </Transition>
    </BootstrapColumn>
</div>

<ModalWindow {isOpen}
             onDismiss={close}
             on:click={close}
             on:dismiss={close}
             title={$dialogTitle}
             body={$dialogBody}
             linkConverter={true}
>
    <hr class="mt-2"/>
    <TextBlock title="To link directly to this converter, use this URL:"
               h2Classes="h6 text-primary"
               text='<code class="text-dark">{currentUrl}/library/databases/#/?link-converter=1</code>'
    />
</ModalWindow>

<style>
    /* Your existing styles */
</style>
